import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import { useEnhancedBrandStore } from '../../../stores/enhancedBrandStore'
import { ManufacturingVerification } from '../../../types/brand'
import { XMarkIcon } from '@heroicons/react/24/outline'
import echarts from '../../../lib/echarts'

const MANUFACTURING_COLORS = {
  confirmed_usa: '#10b981',
  likely_usa: '#84cc16',
  mixed: '#f59e0b',
  imported: '#ef4444',
  unknown: '#71717a'
}

const MANUFACTURING_LABELS = {
  confirmed_usa: 'Confirmed USA',
  likely_usa: 'Likely USA',
  mixed: 'Mixed',
  imported: 'Imported',
  unknown: 'Unknown'
}

export function InteractiveManufacturingChart() {
  const { filteredBrands, allBrands, filters, setManufacturingVerificationFilter } = useEnhancedBrandStore()

  const chartData = useMemo(() => {
    // Get counts from filtered data
    const filteredCounts = filteredBrands.reduce((acc, brand) => {
      if (brand.manufacturing_verification in MANUFACTURING_LABELS) {
        acc[brand.manufacturing_verification] = (acc[brand.manufacturing_verification] || 0) + 1
      }
      return acc
    }, {} as Record<string, number>)

    // Get total counts for context
    const totalCounts = allBrands.reduce((acc, brand) => {
      if (brand.manufacturing_verification in MANUFACTURING_LABELS) {
        acc[brand.manufacturing_verification] = (acc[brand.manufacturing_verification] || 0) + 1
      }
      return acc
    }, {} as Record<string, number>)

    return Object.entries(filteredCounts).map(([key, value]) => ({
      name: MANUFACTURING_LABELS[key as ManufacturingVerification],
      value,
      key: key as ManufacturingVerification,
      totalValue: totalCounts[key] || 0,
      itemStyle: {
        color: MANUFACTURING_COLORS[key as ManufacturingVerification],
        shadowBlur: filters.manufacturingVerification.length > 0 && filters.manufacturingVerification.includes(key as ManufacturingVerification) ? 6 : 0,
        shadowColor: filters.manufacturingVerification.length > 0 && filters.manufacturingVerification.includes(key as ManufacturingVerification) ? 'rgba(0, 0, 0, 0.4)' : 'transparent'
      }
    }))
  }, [filteredBrands, allBrands, filters.manufacturingVerification])

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: (params: any) => {
        const data = params[0]
        const dataItem = chartData.find(item => item.name === data.name)
        if (!dataItem) return data.name
        
        const totalPercentage = ((dataItem.totalValue / allBrands.length) * 100).toFixed(1)
        const filteredPercentage = filteredBrands.length > 0 ? ((data.value / filteredBrands.length) * 100).toFixed(1) : '0'
        
        const hasFilters = filters.amazonPresence.length > 0 || 
                          filters.manufacturingVerification.length > 0 || 
                          filters.priceTier.length > 0 || 
                          filters.productCategories.length > 0 || 
                          filters.states.length > 0
        
        if (hasFilters) {
          return `<strong>${data.name}</strong><br/>` +
                 `${data.value} of ${dataItem.totalValue} brands<br/>` +
                 `<span style="color: #059669;">${totalPercentage}% of all brands</span><br/>` +
                 `<span style="color: #6b7280;">${filteredPercentage}% of filtered view</span>`
        } else {
          return `<strong>${data.name}</strong><br/>${data.value} brands (${totalPercentage}%)`
        }
      }
    },
    xAxis: {
      type: 'category',
      data: chartData.map(item => item.name),
      axisLabel: {
        interval: 0,
        rotate: 45,
        fontSize: 10
      }
    },
    yAxis: {
      type: 'value',
      name: 'Brands',
      nameTextStyle: {
        fontSize: 12
      }
    },
    series: [
      {
        type: 'bar',
        data: chartData,
        barWidth: '60%',
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ],
    grid: {
      top: '10%',
      left: '10%',
      right: '5%',
      bottom: '25%'
    }
  }

  const handleChartClick = (params: any) => {
    const clickedKey = chartData.find(item => item.name === params.name)?.key
    if (!clickedKey) return

    const currentFilters = filters.manufacturingVerification
    const isSelected = currentFilters.includes(clickedKey)

    if (isSelected) {
      setManufacturingVerificationFilter(currentFilters.filter(f => f !== clickedKey))
    } else {
      setManufacturingVerificationFilter([...currentFilters, clickedKey])
    }
  }

  const clearFilter = (filterValue: ManufacturingVerification) => {
    setManufacturingVerificationFilter(filters.manufacturingVerification.filter(f => f !== filterValue))
  }

  const onEvents = {
    click: handleChartClick
  }

  const hasActiveFilters = filters.amazonPresence.length > 0 || 
                          filters.manufacturingVerification.length > 0 || 
                          filters.priceTier.length > 0 || 
                          filters.productCategories.length > 0 || 
                          filters.states.length > 0

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Chart */}
      <div className="space-y-3">
        {hasActiveFilters && (
          <div className="flex items-center space-x-2 text-sm">
            <div className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded text-xs font-medium">
              FILTERED VIEW
            </div>
            <span className="text-gray-600 dark:text-gray-400">
              Showing {filteredBrands.length} of {allBrands.length} brands
            </span>
          </div>
        )}
        <div className="h-64">
          <ReactECharts
            option={option}
            style={{ height: '100%', width: '100%' }}
            onEvents={onEvents}
            echarts={echarts}
          />
        </div>
      </div>

      {/* Active Filters */}
      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Active Manufacturing Filters
          </h4>
          {filters.manufacturingVerification.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No filters applied. Click chart bars to filter.
            </p>
          ) : (
            <div className="space-y-2">
              {filters.manufacturingVerification.map((filter) => (
                <div
                  key={filter}
                  className="flex items-center justify-between bg-green-50 dark:bg-green-900/30 px-3 py-2 rounded-lg"
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: MANUFACTURING_COLORS[filter] }}
                    />
                    <span className="text-sm text-green-700 dark:text-green-300">
                      {MANUFACTURING_LABELS[filter]}
                    </span>
                  </div>
                  <button
                    onClick={() => clearFilter(filter)}
                    className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setManufacturingVerificationFilter([])}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                Clear all manufacturing filters
              </button>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            💡 <strong>Tip:</strong> Click any bar to filter brands by that manufacturing verification type.
          </p>
        </div>
      </div>
    </div>
  )
}